import React, { useRef, useEffect, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import * as THREE from 'three';
import './BlochSphere.css';

function SphereWithArrow({ isScrolling, setDirection, rotationAxisRef, rotationSpeedRef }) {
  const sphereRef = useRef();
  const arrowRef = useRef();

  useFrame(() => {
    const speed = rotationSpeedRef.current;

    if (sphereRef.current && speed > 0.0001) {
      sphereRef.current.rotateOnAxis(rotationAxisRef.current, speed);
      rotationSpeedRef.current *= 0.95;
    }

    const direction = new THREE.Vector3(
      Math.sin(sphereRef.current.rotation.y),
      Math.sin(sphereRef.current.rotation.x),
      Math.cos(sphereRef.current.rotation.y)
    ).normalize();

    if (arrowRef.current) {
      arrowRef.current.setDirection(direction);
    }

    const theta = Math.acos(direction.y);
    const phi = Math.atan2(direction.z, direction.x);
    setDirection({
      theta: (theta * 180) / Math.PI,
      phi: (phi * 180) / Math.PI,
    });
  });

  return (
    <group ref={sphereRef}>
      <mesh>
        <sphereGeometry args={[1, 48, 12]} />
        <meshBasicMaterial
        color="rgba(180, 180, 180, 0.5)"
        wireframe
        transparent
        opacity={0.5}
        toneMapped={false}
      />
      </mesh>

      <arrowHelper
        ref={arrowRef}
        args={[new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 0), 1.2, 0xe94560]}
      />

        <Text
          position={[0, 1.3, 0]}
          fontSize={0.2}
          color="rgba(180, 180, 180, 0.5)"
          anchorX="center"
          anchorY="middle"
        >
          |0⟩
        </Text>
        <Text
          position={[0, -1.3, 0]}
          fontSize={0.2}
          color="rgba(180, 180, 180, 0.5)"
          anchorX="center"
          anchorY="middle"
        >
          |1⟩
        </Text>


    </group>
  );
}

export default function BlochSphere({ isScrolling, direction }) {
  const rotationAxisRef = useRef(new THREE.Vector3(0, 1, 0));
  const rotationSpeedRef = useRef(0);
  const [localDir, setLocalDir] = useState({ theta: 0, phi: 0 });

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [targetOffset, setTargetOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [targetScale, setTargetScale] = useState(1);

  useEffect(() => {
    if (isScrolling) {
      const randomOffset = {
        x: (Math.random() - 0.5) * 80,
        y: (Math.random() - 0.5) * 80,
      };
      setTargetOffset(randomOffset);
      setTargetScale(0.85);
      rotationAxisRef.current = new THREE.Vector3(
        Math.random() - 0.5,
        Math.random() - 0.5,
        Math.random() - 0.5
      ).normalize();
      rotationSpeedRef.current = 0.05;
    }
  }, [isScrolling]);

  useEffect(() => {
    let animationFrame;
    const animate = () => {
      setOffset((prev) => {
        const dx = targetOffset.x - prev.x;
        const dy = targetOffset.y - prev.y;
        const newX = Math.abs(dx) < 0.5 ? targetOffset.x : prev.x + dx * 0.1;
        const newY = Math.abs(dy) < 0.5 ? targetOffset.y : prev.y + dy * 0.1;
        return { x: newX, y: newY };
      });

      setScale((prev) => {
        const ds = targetScale - prev;
        return Math.abs(ds) < 0.01 ? targetScale : prev + ds * 0.1;
      });

      animationFrame = requestAnimationFrame(animate);
    };
    animate();

    return () => cancelAnimationFrame(animationFrame);
  }, [targetOffset, targetScale]);

  return (
    <div
      className="bloch-wrapper"
      style={{
        transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      <Canvas
        className="bloch-canvas"
        style={{ pointerEvents: 'none' }}
        gl={{ toneMapping: THREE.NoToneMapping }}
      >
        <ambientLight intensity={0.6} />
        <pointLight position={[5, 5, 5]} />
        <SphereWithArrow
          isScrolling={isScrolling}
          setDirection={setLocalDir}
          rotationAxisRef={rotationAxisRef}
          rotationSpeedRef={rotationSpeedRef}
        />
        <OrbitControls enableZoom={false} enablePan={false} enableRotate={false} />
      </Canvas>

      <div className="direction-info">
        θ = {localDir.theta?.toFixed(1)}°, φ = {localDir.phi?.toFixed(1)}°
      </div>
    </div>
  );
}
